import React from 'react';
import c from 'classnames';
import { useSelector } from 'react-redux';

import type { ImmutableRootState, Langcodes } from '@uncinc/react-drupal-core/types';

import { DEFAULT_REQUEST_OPTIONS, SearchkitWrapper } from '@uncinc/react-searchkit';
import type { SearchkitWrapperProps } from '@uncinc/react-searchkit/dist/components/Wrapper';

import { APP_OPTIONS, DEFAULT_LANGUAGE, SEARCHKIT_HOST_URL } from '../../config/constants';
import {
  facetAttributes,
  resultAttributes,
  searchAttributes,
  sorting,
} from './config';

// Subcomponents.
import ActiveFacets from './ActiveFacets';
import Facets from './Facets';
import Results from './Results';
import SearchBar from './SearchBar';
import SortBy from './SortBy';
import Stats from './Stats';
import Pagination from './Pagination';

interface SearchAllContentSubcomponents {
  ActiveFacets: typeof ActiveFacets;
  Facets: typeof Facets;
  Results: typeof Results;
  SearchBar: typeof SearchBar;
  SortBy: typeof SortBy;
  Stats: typeof Stats;
  Pagination: typeof Pagination;
}

export type SearchAllContentProps = Omit<
SearchkitWrapperProps,
'connection' | 'indexName' | 'analytics' | 'searchSettings' | 'requestOptions'
>;

const SearchAllContent: React.FC<SearchAllContentProps> & SearchAllContentSubcomponents = (props) => {
  const {
    routing,
    className,
    children,
    ...restProps
  } = props;

  const langcode = useSelector<ImmutableRootState<Langcodes>, Langcodes>((state) => state.getIn(['ui', 'language'], DEFAULT_LANGUAGE));

  const classNames = c('SearchAllContent', className);

  return (
    <SearchkitWrapper
      {...restProps}
      className={classNames}
      connection={{ host: SEARCHKIT_HOST_URL }}
      indexName={APP_OPTIONS.parameters.searchkitIndexAllContent}
      routing={routing}
      analytics
      searchSettings={{
        search_attributes: searchAttributes,
        result_attributes: resultAttributes,
        facet_attributes: facetAttributes,
        sorting,
      }}
      requestOptions={{
        getBaseFilters: () => [
          {
            bool: {
              must: {
                term: {
                  langcode,
                },
              },
            },
          },
        ],
        ...DEFAULT_REQUEST_OPTIONS,
      }}
    >
      {children}
    </SearchkitWrapper>
  );
};

SearchAllContent.ActiveFacets = ActiveFacets;
SearchAllContent.Facets = Facets;
SearchAllContent.Results = Results;
SearchAllContent.SearchBar = SearchBar;
SearchAllContent.SortBy = SortBy;
SearchAllContent.Stats = Stats;
SearchAllContent.Pagination = Pagination;

export default SearchAllContent;
